import * as React from 'react';
import type {
  InvoiceTravelInformation,
} from 'contracts/tracking-v2/invoice-travel-information';
import { useInvoiceStore } from 'pages/tracking-v2/invoice-details-page/hooks';
import {
  getInvoiceSupplierCoordinates,
  getInvoiceCompanyCoordinates,
} from 'pages/tracking-v2/utils/invoice-map-utils';
import {
  getUniqueGeolocations,
} from 'pages/tracking-v2/utils/geolication-utils';

const MAX_WAYPOINTS = 24;

export const useInvoiceTransportMapHelper = () => {
  const { invoice } = useInvoiceStore();

  const [originAndDestinationDirections, setOriginAndDestinationDirections]
    = React.useState<google.maps.DirectionsResult | null>(null);
  const [truckDirections, setTruckDirections]
    = React.useState<google.maps.DirectionsResult | null>(null);
  const [truckAndDestinationDirections, setTruckAndDestinationDirections]
    = React.useState<google.maps.DirectionsResult | null>(null);

  const originCoordinates = React.useMemo((): google.maps.LatLngLiteral | string | undefined => {
    return invoice?.supplier
      ? getInvoiceSupplierCoordinates(invoice.supplier)
      : undefined;
  }, [
    invoice?.supplier,
  ]);

  const destinationCoordinates = React.useMemo((): google.maps.LatLngLiteral | string | undefined => {
    return invoice?.company
      ? getInvoiceCompanyCoordinates(invoice.company)
      : undefined;
  }, [
    invoice?.company,
  ]);

  const originAndDestinationDirectionOptions = React.useMemo((): google.maps.DirectionsRequest | undefined => {
    if (!originCoordinates || !destinationCoordinates) return undefined;

    return {
      origin: originCoordinates,
      destination: destinationCoordinates,
      travelMode: 'DRIVING' as google.maps.TravelMode,
    };
  }, [
    originCoordinates,
    destinationCoordinates,
  ]);

  const handleOriginAndDestinationDirectionsServiceResponse = React.useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => {
    if (status !== 'OK' || !result) return;

    setOriginAndDestinationDirections(result);
  }, []);

  const originPosition = React.useMemo(() => {
    const routeLegs = originAndDestinationDirections?.routes[0]?.legs;
    return routeLegs?.[0]?.start_location;
  }, [originAndDestinationDirections]);

  const destinationPosition = React.useMemo(() => {
    const routeLegs = originAndDestinationDirections?.routes[0]?.legs;
    const lastLeg = routeLegs?.[routeLegs.length - 1];
    return lastLeg?.end_location;
  }, [originAndDestinationDirections]);

  const truckCoordinatesArray = React.useMemo(() => {
    return getUniqueGeolocations({
      geolocations: invoice?.geolocation ?? [],
      maxGeolocations: MAX_WAYPOINTS,
    });
  }, [
    invoice?.geolocation,
  ]);

  const lastTruckPosition = React.useMemo(() => {
    return truckCoordinatesArray[truckCoordinatesArray.length - 1];
  }, [truckCoordinatesArray]);

  const truckDirectionOptions = React.useMemo((): google.maps.DirectionsRequest | undefined => {
    if (!truckCoordinatesArray.length) return undefined;

    return {
      origin: truckCoordinatesArray[0],
      destination: truckCoordinatesArray[truckCoordinatesArray.length - 1],
      waypoints: truckCoordinatesArray.slice(1, -1).map(coordinates => ({
        location: coordinates,
        stopover: false,
      })),
      travelMode: 'DRIVING' as google.maps.TravelMode,
      optimizeWaypoints: false,
      provideRouteAlternatives: false,
    };
  }, [
    truckCoordinatesArray,
  ]);

  const handleTruckDirectionsServiceResponse = React.useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => {
    if (status !== 'OK' || !result) return;

    setTruckDirections(result);
  }, []);

  const truckAndDestinationOptions = React.useMemo((): google.maps.DirectionsRequest | undefined => {
    if (!lastTruckPosition || !destinationCoordinates) return undefined;

    return {
      origin: lastTruckPosition,
      destination: destinationCoordinates,
      travelMode: 'DRIVING' as google.maps.TravelMode,
    };
  }, [lastTruckPosition, destinationCoordinates]);

  const handleTruckAndDestinationDirectionsServiceResponse = React.useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => {
    if (status !== 'OK' || !result) return;

    setTruckAndDestinationDirections(result);
  }, []);

  const invoiceTravelInformation = React.useMemo((): InvoiceTravelInformation | undefined => {
    const totalDistance = (() => {
      if (!originAndDestinationDirections) return undefined;
      const { routes } = originAndDestinationDirections;
      return routes[0]?.legs[0]?.distance?.value;
    })();

    const totalDuration = (() => {
      if (!originAndDestinationDirections) return undefined;
      const { routes } = originAndDestinationDirections;
      return routes[0]?.legs[0]?.duration?.value;
    })();

    const remainingDistance = (() => {
      if (!truckAndDestinationDirections) return undefined;
      const { routes } = truckAndDestinationDirections;
      return routes[0]?.legs[0]?.distance?.value;
    })();

    const traveledDistance = (() => {
      if (totalDistance === undefined || remainingDistance === undefined) {
        return undefined;
      }
      const distance = totalDistance - remainingDistance;
      return distance >= 0 ? distance : 0;
    })();

    const traveledPercentage = (() => {
      if (traveledDistance === undefined || totalDistance === undefined) {
        return 0;
      }

      return (traveledDistance / totalDistance) * 100;
    })();

    if (
      totalDuration === undefined ||
      totalDistance === undefined
    ) {
      return undefined;
    }

    return {
      totalDuration,
      totalDistance,
      remainingDistance,
      traveledDistance,
      traveledPercentage,
    };
  }, [
    originAndDestinationDirections,
    truckAndDestinationDirections,
  ]);

  return {
    originAndDestinationDirections,
    originAndDestinationDirectionOptions,
    handleOriginAndDestinationDirectionsServiceResponse,
    originPosition,
    destinationPosition,
    truckDirections,
    truckDirectionOptions,
    handleTruckDirectionsServiceResponse,
    lastTruckPosition,
    truckAndDestinationOptions,
    handleTruckAndDestinationDirectionsServiceResponse,
    invoiceTravelInformation,
    mapDataIsReady: !!invoice,
  };
};